import { useState } from "react";
import Modal from "../../../components/ui/modal";
import Dropdown from "../../../components/ui/dropdown";
import colors from "../../../utils/colors";
import DateInput from "../../../components/dateInput";
import Button from "../../../components/ui/button";
import { displayErrorMsg } from "../../../utils/displayErrorMsg";
import { TAppointmentDetails, TKey } from "../../../types";
import useValidateStateObj from "../../../hooks/useValidateStateObj";
import { initialStateValue } from "../utils/data";
import { useCreateAppointment } from "../../../apis/doctor";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { useParams } from "react-router-dom";

interface IProps {
  isOpen: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
}

const ScheduleAppointmentModal = ({ isOpen, onClose, title }: IProps) => {
  const { id: patientId } = useParams();
  const { name, _id: doctorId } = useAppSelector(
    (state) => state.auth.doctorInfo
  );
  const [submitting, setSubmitting] = useState(false);

  const [appointmentDetails, setAppointmentDetails] =
    useState<TAppointmentDetails>({
      ...initialStateValue,
      patientId: patientId ?? "",
      doctorName: name ?? "",
      appointmentType: { category: "Initial", mode: "Online" }, // ✅ Valid default values
      appointmentStatus: "Active", // ✅ Valid default value
      //notes: "",
    });

  const { isValid } = useValidateStateObj<TAppointmentDetails>({
    ...appointmentDetails,
    doctorName: name,
  });

  const updateState = (name: TKey, value: string | object) => {
    setAppointmentDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearInputs = () => {
    setAppointmentDetails({
      ...initialStateValue,
      patientId: patientId ?? "",
      appointmentType: { category: "Initial", mode: "Online" },
      appointmentStatus: "Active",
      //notes,
      //notes: appointmentDetails.notes, // ✅ Preserve the updated notes
    });
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose(false);
    clearInputs();
  };

  const { mutate: scheduleAppointment, isPending } = useCreateAppointment({
    handleSuccess: () => {
      handleClose();
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      className="w-[35%] min-w-[400px] max-w-[500px] p-[40px]"
    >
      <div className="flex flex-col gap-4 mt-6">
        <div className="w-full">
          <p className="text-sm mb-2">Patient's ID</p>
          <input
            onChange={(e) => updateState("patientId", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder="Enter patient's id"
            readOnly={!!patientId}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
            value={patientId}
          />
          {displayErrorMsg(
            "Patient's id cannot be empty",
            appointmentDetails.patientId,
            submitting
          )}
        </div>

        <div className="w-full">
          <p className="text-sm mb-2">Doctor's Name</p>
          <input
            onChange={(e) => updateState("doctorName", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder="Enter doctor's name"
            readOnly={!!name}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
            value={name}
          />
          {displayErrorMsg(
            "Doctor's name cannot be empty",
            appointmentDetails?.doctorName?.trim(), // Ensure it's not empty or just spaces
            submitting
          )}
        </div>

        <div className="flex w-full">
          <div className="w-full">
            <p className="text-sm mb-2">Date:</p>

            <DateInput
              value={appointmentDetails.date}
              handleChange={(e) => updateState("date", e ?? "")}
            />
            {displayErrorMsg(
              "Date cannot be empty",
              appointmentDetails.date,
              submitting
            )}
          </div>
        </div>

        <div className="flex justify-between gap-2">
          <div className="flex-1">
            <p className="text-sm mb-2">From:</p>

            <DateInput
              value={appointmentDetails.startTime}
              handleChange={(e) => {
                if (e) {
                  const selectedDate = appointmentDetails.date
                    ? new Date(appointmentDetails.date)
                    : new Date();
                  const selectedTime = new Date(e);

                  selectedDate.setHours(
                    selectedTime.getHours(),
                    selectedTime.getMinutes(),
                    0,
                    0
                  );

                  updateState("startTime", selectedDate);
                }
              }}
              showTimeSelect
              showTimeSelectOnly
              dateFormat="h:mm aa"
            />
            {displayErrorMsg(
              "Start time cannot be empty",
              appointmentDetails.startTime,
              submitting
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm mb-2">To:</p>

            <DateInput
              value={appointmentDetails.endTime}
              handleChange={(e) => {
                if (e) {
                  const selectedDate = appointmentDetails.date
                    ? new Date(appointmentDetails.date)
                    : new Date();
                  const selectedTime = new Date(e);

                  selectedDate.setHours(
                    selectedTime.getHours(),
                    selectedTime.getMinutes(),
                    0,
                    0
                  );

                  updateState("endTime", selectedDate);
                }
              }}
              showTimeSelect
              showTimeSelectOnly
              dateFormat="h:mm aa"
            />
            {displayErrorMsg(
              "End time cannot be empty",
              appointmentDetails.endTime,
              submitting
            )}
          </div>
        </div>

        {/* Appointment Type Selection */}
        <div className="flex w-full gap-4">
          <div className="w-1/2">
            <p className="text-sm mb-2">Category:</p>
            <input
              type="text"
              value={appointmentDetails.appointmentType.category}
              className="!h-[40px] !w-full px-2 border rounded bg-gray-100 cursor-not-allowed"
              disabled
            />
          </div>
          <div className="w-1/2">
            <p className="text-sm mb-2">Mode:</p>
            <Dropdown
              onSelect={(s) =>
                updateState("appointmentType", {
                  ...appointmentDetails.appointmentType,
                  mode: s,
                })
              }
              options={["Online", "Offline"]}
              className="!h-[40px] !w-full"
              placeholder="Select mode"
              value={appointmentDetails.appointmentType.mode}
            />
          </div>
          {displayErrorMsg(
            "Both category and mode are required",
            appointmentDetails.appointmentType.category &&
              appointmentDetails.appointmentType.mode,
            submitting
          )}
        </div>

        {/* Appointment Status Selection */}
        <div className="w-full">
          <p className="text-sm mb-2">Appointment Status:</p>
          <Dropdown
            onSelect={(s) => updateState("appointmentStatus", s)}
            options={["Active", "Missed", "Completed"]}
            className="!h-[40px] !w-full"
            placeholder="Select status"
            value={appointmentDetails.appointmentStatus}
          />
          {displayErrorMsg(
            "Appointment status cannot be empty",
            appointmentDetails.appointmentStatus,
            submitting
          )}
        </div>

        <div className="mb-3 w-full">
          <p className="text-sm mb-2">Appointment Address</p>
          <textarea
            style={{ border: "1px solid", borderColor: colors.grey }}
            onChange={(e) => updateState("address", e.target.value)}
            className="placeholder:text-[#606060] text-[14px] h-[90px] rounded-md outline-none w-full p-2"
            value={appointmentDetails?.address}
          />
        </div>
        <div className="mb-3 w-full">
          <p className="text-sm mb-2">Note</p>
          <textarea
            style={{ border: "1px solid", borderColor: colors.grey }}
            onChange={(e) => updateState("notes", e.target.value)}
            className="placeholder:text-[#606060] text-[14px] h-[90px] rounded-md outline-none w-full p-2"
            value={appointmentDetails?.notes}
          />
        </div>

        <Button
          type="submit"
          text="Schedule New Appointment"
          theme="primary"
          size="md"
          className="rounded-md"
          //disabled={!isValid}
          loading={isPending}
          onClick={() => {
            setSubmitting(true);
            //console.log("Submitting appointment:", appointmentDetails); // Debugging
            scheduleAppointment({
              ...appointmentDetails,
              patientId: Number(appointmentDetails.patientId),
              appointmentStatus: appointmentDetails.appointmentStatus,
              notes: appointmentDetails?.notes,
              address: appointmentDetails?.address,
              doctorName: appointmentDetails?.doctorName ?? name,
              doctorId,
            });
          }}
        />
      </div>
    </Modal>
  );
};

export default ScheduleAppointmentModal;
